import { useEffect, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Check, Code2, Copy, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTimezone } from '@/components/TimezoneContext';
import { Icon } from '@/trace/icons';

// Shared primitives, rendered in the Trace prototype's markup so every directory and configuration screen reads
// like the prototype while staying on live data.

const TONE = { green: 'good', blue: 'blue-badge', amber: 'warn', red: 'bad', quiet: '' };
export function Status({ children, tone = 'quiet' }) {
  return <span className={`badge${TONE[tone] ? ` ${TONE[tone]}` : ''}`} data-tone={tone}>{children}</span>;
}

// Date over time, in the chosen display timezone.
export function DateStamp({ value }) {
  const { formatDate, formatTime } = useTimezone();
  if (!value) return <span className="sp-date"><span>—</span></span>;
  return <span className="sp-date"><span>{formatDate(value)}</span><small>{formatTime(value)}</small></span>;
}

export const initialsOf = (name, fallback = '?') => {
  const words = (name || '').trim().split(/\s+/).filter(w => /^\p{L}/u.test(w));
  return words.length ? words.map(w => w[0]).slice(0, 2).join('').toUpperCase() : fallback;
};

export function Person({ name, email }) {
  return (
    <span className="journey-profile">
      <span className="avatar">{initialsOf(name || email)}</span>
      <span><strong>{name || email || 'Anonymous visitor'}</strong>{name && email && <small>{email}</small>}</span>
    </span>
  );
}

// The prototype's metric cell: label, figure, one short foot line.
export function Metric({ label, value, detail }) {
  return (
    <div className="metric">
      <div className="metric-label">{label}</div>
      <div className="metric-main"><strong className="metric-value">{value ?? '—'}</strong></div>
      {detail && <div className="metric-foot"><span>{detail}</span></div>}
    </div>
  );
}

// The page heading: the page name as its eyebrow, the title, an optional description, actions on the right.
export function PageIntro({ eyebrow, title, description, actions }) {
  const label = eyebrow ?? (typeof title === 'string' ? title.toUpperCase() : null);
  return (
    <div className="page-heading">
      <div>{label && <div className="eyebrow">{label}</div>}<h1>{title}</h1>{description && <p>{description}</p>}</div>
      {actions && <div className="heading-actions">{actions}</div>}
    </div>
  );
}

export function SurfaceHead({ eyebrow, title, children }) {
  return <div className="panel-head"><div><h2>{title}</h2>{eyebrow && <p>{eyebrow}</p>}</div>{children}</div>;
}

export function PageTabs({ label, tabs, active, onChange }) {
  return <div className="table-tabs" role="group" aria-label={label}>
    {tabs.map(tab => <button key={tab.id} type="button" className={active === tab.id ? 'selected' : ''} aria-pressed={active === tab.id}
      onClick={() => onChange(tab.id)}>{tab.label}{tab.count != null && <span className="count">{tab.count}</span>}</button>)}
  </div>;
}

export function EmptyState({ icon: IconComponent = Code2, title, description, actions }) {
  return <div className="empty-state">
    <IconComponent size={28} aria-hidden="true" />
    <h3>{title}</h3>{description && <p>{description}</p>}{actions}
  </div>;
}

export function QueryError({ onRetry, message = 'Could not load this page. Your data has not been changed.' }) {
  return <div className="query-error" role="alert"><p>{message}</p>
    <button type="button" className="btn" onClick={onRetry}>Try again</button></div>;
}

export function IntegrationNote({ title, endpoint, copy }) {
  return (
    <div className="notice">
      <strong>{title}</strong> — {copy} <code className="type-id">{endpoint}</code>
    </div>
  );
}

// Record directories share the prototype's table: sentence-case heads, 57px rows, the arrow that opens a record.
export function RecordTable({ rows, columns, label, onOpen, isRowInteractive = () => true, openName, rowKey = r => r.id, emptyText = 'No records match these filters.', foot }) {
  return (
    <div className="sp-table-wrap">
      <div className="table-scroll">
        <table className="data-table" aria-label={label}>
          <thead><tr>{columns.map(c => <th key={c.key} className={c.numeric ? 'num' : undefined}>{c.label}</th>)}<th><span className="sr-only">Details</span></th></tr></thead>
          <tbody>
            {rows.length ? rows.map(row => {
              const interactive = !!onOpen && isRowInteractive(row);
              return <tr key={rowKey(row)} tabIndex={interactive ? 0 : undefined} data-interactive={interactive} aria-label={interactive ? openName?.(row) : undefined}
                onClick={interactive ? e => { if (!e.target.closest('button, a, input, select, textarea, [role="checkbox"]')) onOpen(row); } : undefined}
                onKeyDown={interactive ? e => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpen(row); } } : undefined}>
                {columns.map(c => <td key={c.key} data-label={c.label} className={`${c.className || ''}${c.numeric ? ' num' : ''}`.trim() || undefined}>{c.render(row)}</td>)}
                <td className="row-open">{interactive && <button type="button" className="btn ghost icon-btn" aria-label={openName?.(row) || 'Open record'} onClick={() => onOpen(row)}><Icon name="arrowup" small /></button>}</td>
              </tr>;
            }) : <tr><td colSpan={columns.length + 1}><div className="empty-state" role="status"><p>{emptyText}</p></div></td></tr>}
          </tbody>
        </table>
      </div>
      {foot && <div className="table-bottom">{foot}</div>}
    </div>
  );
}

export const money = (n, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: (currency || 'USD').toUpperCase(), maximumFractionDigits: 2 }).format(n || 0);
export const count = n => (n == null ? '—' : Number(n).toLocaleString('en-US'));

// The prototype's right-hand drawer as a Radix dialog (focus trap, Esc, outside click), rendered inside the app
// root so the prototype styles apply.
export function Drawer({ open, onClose, eyebrow, label, actions, testId, closeTestId, wide, children }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogPrimitive.Portal container={document.querySelector('.sp-app') || undefined}>
        <DialogPrimitive.Overlay className="overlay drawer-overlay">
          <DialogPrimitive.Content className={`drawer${wide ? ' drawer-wide' : ''}`} data-testid={testId} aria-describedby={undefined}>
            <DialogPrimitive.Title className="sr-only">{label}</DialogPrimitive.Title>
            <div className="drawer-head sp-drawer-head">
              <div className="between"><span className="label">{eyebrow}</span>
                <span className="sp-drawer-actions">{actions}
                  <DialogPrimitive.Close asChild>
                    <button type="button" className="btn ghost icon-btn" data-testid={closeTestId} aria-label="Close details"><X size={16} /></button>
                  </DialogPrimitive.Close>
                </span>
              </div>
            </div>
            {children}
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export function DrawerTabs({ tabs, active, onChange, label = 'Record details' }) {
  const move = (e, index) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const next = e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1
      : (index + (e.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    e.currentTarget.parentElement.querySelectorAll('[role="tab"]')[next]?.focus();
    onChange(tabs[next].id);
  };
  return (
    <div className="table-tabs sp-drawer-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab, index) => (
        <button key={tab.id} type="button" role="tab" aria-selected={active === tab.id} className={active === tab.id ? 'selected' : ''}
          tabIndex={active === tab.id ? 0 : -1} data-state={active === tab.id ? 'active' : 'inactive'}
          data-testid={tab.testId} onClick={() => onChange(tab.id)} onKeyDown={e => move(e, index)}>
          {tab.label}{tab.badge != null && <span className="count" data-tone={tab.tone}>{tab.badge}</span>}
        </button>
      ))}
    </div>
  );
}

export function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = async () => {
    if (pending.current || text == null || text === '') return;
    pending.current = true;
    setBusy(true);
    setCopied(false);
    clearTimeout(timer.current);
    try {
      await navigator.clipboard.writeText(String(text));
      setCopied(true);
      toast.success('Copied to clipboard', { duration: 1500 });
      timer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy. Select the text and copy it manually.');
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  return (
    <button type="button" className="sp-copy" aria-label={copied ? `${label} — copied` : label}
      title={copied ? 'Copied to clipboard' : label} aria-busy={busy || undefined}
      disabled={busy || text == null || text === ''} data-copied={copied || undefined} onClick={copy}>
      {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
    </button>
  );
}

// A drawer field: label, value (or "Not provided"), optional copy button.
export function Field({ label, value, copyable, wrap }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd data-empty={!value || undefined} data-wrap={wrap || undefined} title={value || undefined}>
        <span className="sp-dd-text">{value || 'Not provided'}</span>{copyable && value && <CopyButton text={value} label={`Copy ${label}`} />}
      </dd>
    </div>
  );
}
